import { useEffect, useRef } from "react";
import { track } from "@/lib/posthog";

interface TrackOnViewProps {
  event: string;
  properties?: Record<string, unknown>;
  /** Pass `false` to skip tracking (e.g. missing required props). */
  enabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps children in a div and fires a PostHog event the first time it
 * intersects the viewport. Fires at most once per mount.
 */
const TrackOnView = ({
  event,
  properties,
  enabled = true,
  children,
  className,
}: TrackOnViewProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || firedRef.current || !ref.current) return;
    const node = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            track(event, properties);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [event, enabled, properties]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

export default TrackOnView;
